import { Component, EventEmitter, Inject, inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, } from '@angular/core';

import { MatTableDataSource } from '@angular/material/table';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import fileDialog from 'file-dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { SnackbarConstants } from '@lmc-app/shared/constants/snackbar.constant';
import { DeleteDocumentDialog } from '@lmc-app/applications/delete-document-dialog';
import { Loading } from '@lmc-app/applications/loading';
import { ApplicationService } from '@lmc-app/core/services/application.service';
import { UserService } from '@lmc-app/core/services/user.service';
import { DateUtil } from '@lmc-app/shared/utils/date.util';

@Component({
	selector: 'app-workdrive',
	templateUrl: './workdrive.component.html',
	styleUrls: ['./workdrive.component.scss'],
	providers: [],
})
export class WorkdriveComponent implements OnInit, OnChanges {
	constructor(
		private readonly applicationService: ApplicationService,
		private readonly userService: UserService,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: any
	) {}

	@Input() application: any;
	@Output() applicationChange = new EventEmitter<any>();

	private readonly _bottomSheet = inject(MatBottomSheet);
	private readonly _dialog = inject(MatDialog);
	private readonly _snackBar = inject(MatSnackBar);

	currentView: 'list' | 'grid' = 'grid';

	mainAppDocuments: any[] = [];
	coAppDocuments: any[] = [];
	currentUser: any;

	// Loading states
	isLoading = true;
	hasError = false;
	errorMessage = '';

	displayedColumnsDocuments: string[] = [
		'name',
		'type',
		'size',
		'uploadedInfo',
		'action',
	];
	dataSourceMainAppDocuments = new MatTableDataSource(this.mainAppDocuments);
	dataSourceCoAppDocuments = new MatTableDataSource(this.coAppDocuments);

	ngOnInit() {
		if(this.data?.isCollectorView && this.data?.type === "workdrive"){
			this.application = this.data?.appData?.application;
			this.getDocumentsByApplicationId(this.data?.appData?.application_id);
;		}
		this.userService.user$.subscribe((user) => {
			this.currentUser = user;
		});

		const savedView = localStorage.getItem('preferredDocumentView');
		if (savedView && (savedView === 'list' || savedView === 'grid')) {
			this.currentView = savedView;
		}

		this.getWorkDriveFolderId();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.application) {
			this.application = changes.application?.currentValue;
			if (this.application) {
				this.getDocumentsByApplicationId(this.application.id);
			}
		}
	}

	saveViewPreference() {
		localStorage.setItem('preferredDocumentView', this.currentView);
	}

	onViewChange() {
		this.saveViewPreference();
	}

	// Getter methods for template
	get showStateDisplay(): boolean {
		return (
			this.isLoading ||
			this.hasError ||
			(this.mainAppDocuments.length === 0 && this.coAppDocuments.length === 0)
		);
	}

	get stateType(): 'loading' | 'empty' | 'error' {
		if (this.isLoading) return 'loading';
		if (this.hasError) return 'error';
		return 'empty';
	}

	get showContent(): boolean {
		return (
			!this.isLoading &&
			!this.hasError &&
			(this.mainAppDocuments.length > 0 || this.coAppDocuments.length > 0)
		);
	}

	get canUploadDocuments(): boolean {
		// Can upload if either Workdrive Folder ID exists OR Account_Name exists
		return !!(
			this.application?.zohoworkdriveforcrm__Workdrive_Folder_ID ||
			this.application?.Account_Name
		);
	}

	get uploadDisabledMessage(): string {
		return 'Please fill in the agency information before uploading documents';
	}

	getDocumentsByApplicationId(applicationId: string) {
		this.isLoading = true;
		this.hasError = false;
		this.errorMessage = '';

		this.applicationService.getDocumentsByApplicationId(applicationId).subscribe({
			next: (res) => {
				this.mainAppDocuments = res.mainApplicant;
				this.coAppDocuments = res.coApplicant;
				this.dataSourceMainAppDocuments.data = this.mainAppDocuments;
				this.dataSourceCoAppDocuments.data = this.coAppDocuments;
				this.isLoading = false;
			},
			error: (error) => {
				console.error(error);
				this.hasError = true;
				this.errorMessage = 'Failed to load documents';
				this.isLoading = false;
			},
		});
	}

	documentOnClick(resourceId: string) {
		if(this.data?.isCollectorView && this.data?.type === "workdrive" && this.data?.external){
			this.applicationService.getWorkdriveExternalLink(resourceId).subscribe({
				next: (res) => {					
					const externalLink = res.link; 
					if (externalLink) window.open(externalLink, '_blank');
				},
				error: (error) => {
					console.error(error);
					this.hasError = true;
					this.errorMessage = 'Failed to load external link';
				}	
			})
		}else{
			window.open('https://docs.lmcredit.com/file/' + resourceId);
		}
	}

	downloadDocument(resourceId: string) {
		window.open(
			'https://download-accl.zoho.com/v1/workdrive/download/' + resourceId
		);
	}

	deleteDocument(
		documentOwner: string,
		type: string,
		fileName: string,
		resourceId: string
	) {
		this._dialog
			.open(DeleteDocumentDialog, {
				data: {
					type: type,
					fileName: fileName,
				},
			})
			.afterClosed()
			.subscribe((result) => {
				console.log(result);
				if (result) {
					this.applicationService
						.deleteDocumentByResourceId(resourceId)
						.subscribe({
							next: (response) => {
								switch (documentOwner) {
									case 'MAIN_APPLICANT': {
										this.mainAppDocuments = this.mainAppDocuments.filter(
											(doc) => doc.id !== resourceId
										);
										this.dataSourceMainAppDocuments.data =
											this.mainAppDocuments;
										break;
									}
									case 'CO_APPLICANT': {
										this.coAppDocuments = this.coAppDocuments.filter(
											(doc) => doc.id !== resourceId
										);
										this.dataSourceCoAppDocuments.data = this.coAppDocuments;
										break;
									}
								}
								this._snackBar.open(
									'Delete file success',
									SnackbarConstants.ACTION_DISMISS,
									SnackbarConstants.DURATION_DISMISS_SHORT
								);
							},
							error: (error) => {
								console.error(error);
								this._snackBar.open(
									'Delete file failure',
									SnackbarConstants.ACTION_DISMISS,
									SnackbarConstants.DURATION_DISMISS_SHORT
								);
							},
						});
				}
			});
	}

	openZohoWorkdrive() {
		window.open(
			'https://docs.lmcredit.com/folder/' +
				this.application.zohoworkdriveforcrm__Workdrive_Folder_ID
		);
	}

	uploadFile(documentOwner: string, type: string) {
		fileDialog({ multiple: true }, (files) => {
			// Validate file count limit
			if (files.length > 15) {
				this._snackBar.open(
					'Maximum 15 files allowed. Please select up to 15 files only.',
					SnackbarConstants.ACTION_DISMISS,
					SnackbarConstants.DURATION_DISMISS_LONG
				);
				return;
			}

			this._bottomSheet.open(Loading, {
				disableClose: true,
				data: { message: 'Uploading documents' },
			});
			const formData = new FormData();
			if (this.application.zohoworkdriveforcrm__Workdrive_Folder_ID) {
				formData.append(
					'folderId',
					this.application.zohoworkdriveforcrm__Workdrive_Folder_ID
				);
			} else {
				formData.append('applicationId', this.application.id);
				formData.append('agencyId', this.application.Account_Name.id);
				formData.append('name', this.application.Contact_Name.Full_Name);
			}
			formData.append('documentOwner', documentOwner);
			formData.append('uploadedBy', this.currentUser.full_name);
			Array.from(files).forEach((file) => {
				formData.append(type, file);
			});
			this.applicationService.uploadDocumentToApplication(formData).subscribe({
				next: (response) => {
					this._bottomSheet.dismiss();
					this.application.zohoworkdriveforcrm__Workdrive_Folder_ID =
						response.folderId;
					this.getDocumentsByApplicationId(this.application.id);
					this._snackBar.open(
						'Upload file success',
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
				},
				error: (err) => {
					console.error(err);
					this._bottomSheet.dismiss();
					this._snackBar.open(
						'Upload file failure',
						SnackbarConstants.ACTION_DISMISS,
						SnackbarConstants.DURATION_DISMISS_SHORT
					);
				},
			});
		});
	}

	getDocumentType(type: string): string {
		switch (type) {
			case 'BANK_STATEMENT':
				return 'Bank statement';
			case 'ID':
				return 'ID';
			case 'NOA':
				return 'NOA';
			case 'PAYSTUB':
				return 'Paystub';
			case 'LETTER_OF_EMPLOYMENT':
				return 'Letter of employment';
			case 'SPEAK_ON_BEHALF':
				return 'Speak on behalf';
			case 'APPLY_ON_BEHALF':
				return 'Apply on behalf';
			case 'CONSENT_FORM':
				return 'Consent form';
			case 'VOID_CHEQUE':
				return 'Void cheque';
			case 'BALANCE_LETTER':
				return 'Balance letter';
			default:
				return 'Other';
		}
	}

	// Check workdrive folder if webhook from docusign or manual upload created the folder
	getWorkDriveFolderId(){
		if(!this.application.zohoworkdriveforcrm__Workdrive_Folder_ID){
			this.applicationService.getApplication(this.application.id).subscribe({
			next: (res) => {
				this.application.zohoworkdriveforcrm__Workdrive_Folder_ID = res?.zohoworkdriveforcrm__Workdrive_Folder_ID;
			}
			})
		} 
	}
}
